const STORAGE_KEY = 'JOURNEY_DESIGNER_RETURN_CONTEXT';
const SERVICE_UPDATED_FLAG = 'JOURNEY_DESIGNER_SERVICE_UPDATED';

/** How to restore the step-design UI when returning from Control Plane (service) flows. */
export type JourneyDesignerServiceReturnResume =
  | { kind: 'none' }
  | {
    kind: 'step';
    stepNodeId: string;
    stepName?: string;
    /** Flow item that was highlighted when the user left the canvas. */
    selectedFlowItemNodeId?: string;
  }
  | {
    kind: 'cem';
    /** Parent step the CEM belongs to; re-entered before the CEM is opened. */
    stepNodeId: string;
    stepName?: string;
    /** Flow item in the parent step whose CEM was being edited. */
    cemRootNodeId: string;
    /** Flow item inside the CEM that was highlighted when the user left the canvas. */
    selectedFlowItemNodeId?: string;
  };

export interface JourneyDesignerReturnContext {
  /** Legacy full URL when the user left the canvas; prefer {@link buildJourneyDesignerFlowViewUrl}. */
  returnPath: string;
  journeyName: string;
  journeyVersion: number;
  mode: string;
  timestamp: number;
  /** Re-open this step in the flow editor and show the service sidebar (when set). */
  serviceReturnResume?: JourneyDesignerServiceReturnResume;
  /** Which service sidebar was used when navigating away (defaults to all services). */
  resumeSidebar?: 'ALL_SERVICES' | 'ASSOCIATED_SERVICES';
}

/**
 * Canonical journey canvas URL for the flow view (avoids landing on list or stale paths).
 * When the saved resume context targets a specific step (and we know its name), the URL includes
 * the `/steps/:stepName` segment so a refresh / direct navigation lands on that step.
 * @param queryAppend e.g. `jdReturn=1` for post-service navigation handling
 */
export const buildJourneyDesignerFlowViewUrl = (
  ctx: Pick<JourneyDesignerReturnContext, 'mode' | 'journeyName' | 'journeyVersion' | 'serviceReturnResume'>,
  queryAppend?: string
): string => {
  const base = `/journey-designer/journeys/${ctx.mode}/${encodeURIComponent(ctx.journeyName)}/${ctx.journeyVersion}`;
  const resume = ctx.serviceReturnResume;
  const stepSegment = (resume?.kind === 'step' || resume?.kind === 'cem') && resume.stepName
    ? `/steps/${encodeURIComponent(resume.stepName)}`
    : '';
  const path = `${base}${stepSegment}`;
  return queryAppend ? `${path}?${queryAppend}` : path;
};

const MAX_AGE_MS = 2 * 60 * 60 * 1000; // 2 hours

export const saveJourneyDesignerReturnContext = (context: Omit<JourneyDesignerReturnContext, 'timestamp'>): void => {
  const data: JourneyDesignerReturnContext = { ...context, timestamp: Date.now() };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

export const getJourneyDesignerReturnContext = (): JourneyDesignerReturnContext | null => {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  try {
    const context: JourneyDesignerReturnContext = JSON.parse(raw);
    if (Date.now() - context.timestamp > MAX_AGE_MS) {
      clearJourneyDesignerReturnContext();
      return null;
    }
    return context;
  } catch {
    clearJourneyDesignerReturnContext();
    return null;
  }
};

export const clearJourneyDesignerReturnContext = (): void => {
  sessionStorage.removeItem(STORAGE_KEY);
};

export const hasJourneyDesignerReturnContext = (): boolean => getJourneyDesignerReturnContext() !== null;

export const setServiceUpdatedFlag = (): void => {
  sessionStorage.setItem(SERVICE_UPDATED_FLAG, 'true');
};

export const consumeServiceUpdatedFlag = (): boolean => {
  const flag = sessionStorage.getItem(SERVICE_UPDATED_FLAG);
  if (flag) {
    sessionStorage.removeItem(SERVICE_UPDATED_FLAG);
    return true;
  }
  return false;
};

/** Router state written when navigating back to the journey canvas from a service flow. */
export interface JourneyDesignerServiceReturnLocationState {
  jdServiceReturn: true;
  jdResume: JourneyDesignerServiceReturnResume;
  jdResumeSidebar: 'ALL_SERVICES' | 'ASSOCIATED_SERVICES';
}
